import { useMutation } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  ExternalLink,
  FileSpreadsheet,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { useGoogleSheetsStatus } from '@/Domain/hooks/Integration/useGoogleSheetsStatus.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

const OAUTH_ERROR_PARAM = 'google_sheets';
const OAUTH_ERROR_VALUES = ['error', 'provisioning_error'];

export const GoogleSheetsConnectDialog = () => {
  const { isOpen } = useModalStore();

  if (!isOpen(MODAL_NAMES.GOOGLE_SHEETS_DIALOG)) return null;

  return <GoogleSheetsConnectDialogContent />;
};

const GoogleSheetsConnectDialogContent = () => {
  const { t } = useTranslation('actions_page');
  const { t: tCommon } = useTranslation('common');
  const { closeModal } = useModalStore();
  const { data: status } = useGoogleSheetsStatus();
  const [searchParams, setSearchParams] = useSearchParams();

  // Captured once so the alert survives clearing the param below; else reopening re-shows a stale error.
  const [hasOauthError] = useState(() =>
    OAUTH_ERROR_VALUES.includes(searchParams.get(OAUTH_ERROR_PARAM) || ''),
  );

  useEffect(() => {
    if (!searchParams.has(OAUTH_ERROR_PARAM)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(OAUTH_ERROR_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const googleSheetsOauthMutation = useMutation<{ url: string }>({
    mutationKey: [QueryKey.integration.googleSheetsOauthMutation],
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
  const startOauth = () => googleSheetsOauthMutation.mutate();

  const isConnected = status?.has_token;
  const actionMissing = isConnected && status?.has_action === false;

  return (
    <Dialog open onOpenChange={closeModal}>
      <DialogContent className="max-w-lg" data-testid="google-sheets-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="size-5 text-success" />
            {t('google_sheets.title')}
          </DialogTitle>
          <DialogDescription>{t('google_sheets.description')}</DialogDescription>
          <a
            href="https://docs.quickchat.ai/ai-agent/google-sheets/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm font-medium text-foreground"
          >
            {tCommon('learn')}
            <ArrowUpRight className="size-4" />
          </a>
        </DialogHeader>

        {hasOauthError && (
          <Alert variant="destructive" data-testid="google-sheets-connect-error">
            <AlertCircle className="size-4" />
            <AlertDescription>{t('google_sheets.connect_error')}</AlertDescription>
          </Alert>
        )}

        {isConnected ? (
          <div className="space-y-4" data-testid="google-sheets-connected">
            {actionMissing ? (
              <>
                <Alert variant="warning" data-testid="google-sheets-action-removed">
                  <AlertTriangle className="size-4" />
                  <AlertTitle>{t('google_sheets.action_removed_title')}</AlertTitle>
                  <AlertDescription>{t('google_sheets.action_removed')}</AlertDescription>
                </Alert>
                <Button
                  onClick={startOauth}
                  disabled={googleSheetsOauthMutation.isPending}
                  data-testid="google-sheets-reconnect-button"
                >
                  <RefreshCw className="size-4" />
                  {t('google_sheets.reconnect_and_readd')}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('google_sheets.connected_description')}
              </p>
            )}

            <div className="flex items-center justify-between gap-3">
              {status?.sheet_url && (
                <a
                  href={status.sheet_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-info font-medium"
                  data-testid="google-sheets-open-sheet"
                >
                  <ExternalLink className="size-4" />
                  {t('google_sheets.open_sheet')}
                </a>
              )}
              {!actionMissing && (
                <Button
                  variant="ghost"
                  onClick={startOauth}
                  disabled={googleSheetsOauthMutation.isPending}
                  data-testid="google-sheets-reconnect-button"
                >
                  <RefreshCw className="size-4" />
                  {t('google_sheets.reconnect')}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              className="rounded-lg border border-border bg-muted p-3 space-y-2.5"
              data-testid="google-sheets-before-connect"
            >
              <p className="text-sm font-medium text-foreground">
                {t('google_sheets.before_connect_title')}
              </p>
              <div className="flex items-start gap-2.5 text-sm text-muted-foreground">
                <FileSpreadsheet className="size-4 mt-0.5 shrink-0 text-success" />
                <span>{t('google_sheets.before_connect_sheet')}</span>
              </div>
              <div className="flex items-start gap-2.5 text-sm text-muted-foreground">
                <Zap className="size-4 mt-0.5 shrink-0 text-success" />
                <span>{t('google_sheets.before_connect_action')}</span>
              </div>
            </div>

            <Button
              onClick={startOauth}
              disabled={googleSheetsOauthMutation.isPending}
              data-testid="google-sheets-connect-button"
            >
              <FileSpreadsheet className="size-4" />
              {t('google_sheets.connect')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
